import React, { useState, useRef, useEffect } from 'react';
import { chatbotService, fallbackResponses } from '../../services/chatbotApi';
import './FloatingChatbot.css';

const FloatingChatbot = ({ isOpen, onToggle }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);

  // Storage keys for chat persistence
  const CHAT_STORAGE_KEY = 'yovo_floating_chatbot_messages';
  const CONVERSATION_STORAGE_KEY = 'yovo_floating_chatbot_conversation';

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history from localStorage on component mount
  useEffect(() => {
    try {
      const savedMessages = localStorage.getItem(CHAT_STORAGE_KEY);
      const savedConversation = localStorage.getItem(CONVERSATION_STORAGE_KEY);
      
      if (savedMessages) {
        const parsedMessages = JSON.parse(savedMessages);
        setMessages(parsedMessages);
      }
      
      if (savedConversation) {
        const parsedConversation = JSON.parse(savedConversation);
        setConversationHistory(parsedConversation);
      }
      
      // If no saved messages, show welcome with demo data
      if (!savedMessages || JSON.parse(savedMessages).length === 0) {
        setMessages([
          {
            from: 'user',
            text: 'Hey Yovo help me create and itinerary for Thailand Trip',
            timestamp: new Date()
          },
          {
            from: 'bot',
            text: `Here's a **5-day fun & balanced itinerary for Thailand**, perfect for a family, couple, or solo traveler — with a mix of culture, nature, adventure, and relaxation:

🌴 Thailand 5-Day Itinerary

**Ideal for:** August | Moderate budget | Family / Couples / Solo
**Base locations:** Ubud + Seminyak

🗓️ Day 1 – Arrival + Sukhothai Vibes

* ✈️ Arrive at Thailand International Airport (Thailand)
* 🚗 Transfer to Ubud (~1.5 hrs)`,
            timestamp: new Date()
          },
          {
            from: 'user',
            text: 'Help me book a flight for same',
            timestamp: new Date()
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  }, []);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
      } catch (error) {
        console.error('Error saving messages to localStorage:', error);
      }
    }
  }, [messages]);

  // Save conversation history to localStorage whenever it changes
  useEffect(() => {
    if (conversationHistory.length > 0) {
      try {
        localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(conversationHistory));
      } catch (error) {
        console.error('Error saving conversation history to localStorage:', error);
      }
    }
  }, [conversationHistory]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = {
      from: 'user',
      text: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const historyForApi = conversationHistory.slice(-10);
      const response = await chatbotService.sendMessage(messageText, historyForApi);
      
      const botMessage = {
        from: 'bot',
        text: response.success ? response.message : response.message,
        timestamp: new Date(),
        error: !response.success ? response.error : null
      };

      setMessages(prev => [...prev, botMessage]);
      
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: messageText },
        { role: 'assistant', content: botMessage.text }
      ]);

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        from: 'bot',
        text: fallbackResponses.error,
        timestamp: new Date(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="floating-chatbot-container">
      <div className="floating-chatbot">
        <div className="chatbot-header">
          <span className="chatbot-title">Chat with Yovo</span>
          <button className="close-button" onClick={onToggle} aria-label="Close chatbot">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="chat-content">
          <div className="messages-container">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message-wrapper ${msg.from}`}>
                <div className={`message-bubble ${msg.from}`}>
                  <span className="message-text">
                    {msg.text.split('\n').map((line, index) => {
                      // Format text with basic styling
                      if (line.includes('**') && line.includes('**')) {
                        const parts = line.split('**');
                        return (
                          <span key={index}>
                            {parts.map((part, i) => 
                              i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                            )}
                            {index < msg.text.split('\n').length - 1 && <br />}
                          </span>
                        );
                      }
                      return (
                        <span key={index}>
                          {line}
                          {index < msg.text.split('\n').length - 1 && <br />}
                        </span>
                      );
                    })}
                  </span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        <div className="input-container">
          <div className="input-wrapper">
            <span className={`input-placeholder ${input ? 'hidden' : ''}`}>Ask me anything travel</span>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="chat-input"
            />
            <button 
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
              className="send-button"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.0673 8.50867L9.50733 4.22867C3.75733 1.34867 1.39733 3.70867 4.27733 9.45867L5.14733 11.1987C5.39733 11.7087 5.39733 12.2987 5.14733 12.8087L4.27733 14.5387C1.39733 20.2887 3.74733 22.6487 9.50733 19.7687L18.0673 15.4887C21.9073 13.5687 21.9073 10.4287 18.0673 8.50867ZM14.8373 12.7487H9.43733C9.02733 12.7487 8.68733 12.4087 8.68733 11.9987C8.68733 11.5887 9.02733 11.2487 9.43733 11.2487H14.8373C15.2473 11.2487 15.5873 11.5887 15.5873 11.9987C15.5873 12.4087 15.2473 12.7487 14.8373 12.7487Z" fill="white"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingChatbot;
